import { Component, HostListener, inject } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { getClientTimezone } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import { OperationInfo } from 'src/app/common/long-operations/long-operations.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { setDropdownWidth } from 'src/app/forms/forms.helpers';
import { RegistrationForm } from '../auth-form.inteface';
import { AuthShared } from '../auth-shared.abstract';
import { AccountType } from '../auth.enum';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
})
export class RegistrationComponent extends AuthShared<RegistrationForm> {
  public readonly DEBOUNCE_TIME: number = AppValues.DEBOUNCE_TIME;

  public selectedItem = '';
  public accountType: typeof AccountType = AccountType;
  public accountTypeOptions: AccountType[] = [AccountType.INDIVIDUAL, AccountType.ORGANIZATION];

  private operationInfoService = inject(OperationInfoService);

  @HostListener('click')
  public clickControl(): void {
    setDropdownWidth('.form-select');
  }

  @HostListener('window:resize')
  public resizeWindow(): void {
    setDropdownWidth('.form-select');
  }

  public register(): void {
    if (this.form.invalid) {
      this.validateAllFields();
      return;
    }

    const OPERATION_INFO: OperationInfo = {
      spinner: 'global',
      button: { id: 'registration-form-register-btn' },
    };

    const { email, alias, password, agreeCheck, organizationName } = this.form.getRawValue();

    if (this.isIndividualAccountType()) {
      this.operationInfoService.setupOperation(OPERATION_INFO, () => {
        this.authService.registerIndividual(
          {
            agree: agreeCheck,
            email: email.trim(),
            alias: alias.trim(),
            password: password.trim(),
          },
          getClientTimezone(),
        );
      });
    } else if (this.isOrganizationAccountType()) {
      this.operationInfoService.setupOperation(OPERATION_INFO, () => {
        this.authService.registerOrganization(
          {
            agree: agreeCheck,
            email: email.trim(),
            password: password.trim(),
            organizationName: organizationName.trim(),
          },
          getClientTimezone(),
        );
      });
    }
  }

  public selectCurrentValue(currentValue: string): void {
    this.selectedItem = currentValue;
    this.form.controls.accountType.setValue(currentValue);
  }

  protected initializeForm(): void {
    const aliasValidators: ValidatorFn[] = this.validationService.getValidators('alias');
    aliasValidators.push(
      this.validationService.getRequiredIfSelectionEqualsToValidator('accountType', AccountType.INDIVIDUAL),
    );

    const organizationNameValidators: ValidatorFn[] = this.validationService.getValidators('organizationName');
    organizationNameValidators.push(
      this.validationService.getRequiredIfSelectionEqualsToValidator('accountType', AccountType.ORGANIZATION),
    );

    this.form = new FormGroup(
      {
        email: new FormControl('', this.validationService.getValidators('email')),
        accountType: new FormControl('', this.validationService.getValidators('accountType')),
        alias: new FormControl('', aliasValidators),
        organizationName: new FormControl('', organizationNameValidators),
        password: new FormControl('', this.validationService.getValidators('password')),
        repeatPassword: new FormControl('', this.validationService.getValidators('repeatPassword')),
        agreeCheck: new FormControl({ value: false, disabled: false }, Validators.requiredTrue),
      },

      this.validationService.getFormPasswordValidators(),
    );

    this.validationService.setMaxInputLength(this.form, 'email');
    this.validationService.setMaxInputLength(this.form, 'alias');
    this.validationService.setMaxInputLength(this.form, 'organizationName');

    this.validateRepeatPasswordChanges();
  }

  private isIndividualAccountType(): boolean {
    return this.form.value.accountType === AccountType.INDIVIDUAL;
  }

  private isOrganizationAccountType(): boolean {
    return this.form.value.accountType === AccountType.ORGANIZATION;
  }
}
